import { twMerge } from "tailwind-merge";
import { isFailed, isIgnored, isRunning, TriggerStep } from "~/lib/pipeline";
import {
  StepLinkHighlight,
  StepLink,
} from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import Emojify from "~/components/shared/Emojify";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { StepRow } from "~/components/build/Show/routes/list/-components/StepRow";

interface Props {
  step: TriggerStep;
  depth?: number;
}

export function TriggerStepItem({ step, depth = 0 }: Props) {
  const { missingDependencies } = useStepDependencies(step.uuid);

  const params = useQueryParams();
  const label = step.label || "Trigger build";
  const isStepIgnored = isIgnored(step);

  let highlight: StepLinkHighlight | undefined;
  if (isFailed(step)) {
    highlight = "failed";
  } else if (isRunning(step)) {
    highlight = "running";
  }

  return (
    <StepLink
      to={isStepIgnored ? resolveStepUrl(step.uuid, { open: false }) : resolveStepUrl(step.uuid)}
      type={step.type}
      state={step.state}
      active={step.uuid === params.sid}
      highlight={highlight}
      depth={depth}
      disabled={isStepIgnored}
      title={isStepIgnored ? "Skipped steps cannot be viewed" : undefined}
    >
      <StepRow
        depth={depth}
        stepType={step.type}
        icon={
          <StateIcon
            {...step}
            missingDependencies={missingDependencies}
            className={twMerge("w-4", textColorForStep({ ...step, missingDependencies }))}
          />
        }
        label={<Emojify text={label} />}
        script={
          step.triggerBranch && step.triggerCommit
            ? `${step.triggerBranch}/${step.triggerCommit} on ${step.triggerPipelineSlug}`
            : step.triggerPipelineSlug
        }
      />
    </StepLink>
  );
}

export const createTriggerStepNode = (step: TriggerStep): TreeNode => ({
  id: step.uuid,
  height: STEP_ITEM_HEIGHT,
  render: ({ depth }) => <TriggerStepItem step={step} depth={depth} />,
});
